import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

import { TEACHER_ASSIGNMENT_V2_INSIGHTS_URL } from 'config/urls';
import { filter, forEach, isEmpty, map } from 'lodash';

import { StudentLinkCopyButton } from 'pages/Teachers/shared';
import { colors } from 'theme/palette';
import { calendarDate } from 'utils/date';
import {
  buildGoogleClassroomOptions,
  buildGoogleClassroomWidgetId,
  renderGoogleClassroomWidget
} from 'utils/integrations/googleClassroom';
import { reverse } from 'utils/urls';

import Typography from 'components/Typography';

import styles from './styles.module.scss';

export const AssignmentListByInstance = ({ assignmentDetails }) => {
  const { instances } = assignmentDetails;

  // Important:
  // Soon, we may show all instances, no matter whether they have "active" assignments or not.
  // Revisit this once we decide to do so.
  const instancesWithAssignments = useMemo(
    () => filter(instances, (instance) => !isEmpty(instance.assignments)),
    [instances]
  );

  const buildAssignmentResultsUrl = (sectionId) => {
    return reverse(TEACHER_ASSIGNMENT_V2_INSIGHTS_URL, {
      trackerInstanceSectionId: sectionId
    });
  };

  const getAssignmentName = (assignment) => {
    const assignmentName = (
      <Typography noWrap variant="S-TEXT-1" color={colors.blue1}>
        {assignment.name}
      </Typography>
    );

    if (assignment.deleted) {
      return assignmentName;
    }

    return (
      <Link
        to={buildAssignmentResultsUrl(assignment.id)}
        className={styles.link}
      >
        {assignmentName}
      </Link>
    );
  };

  useEffect(() => {
    forEach(instancesWithAssignments, (instance) =>
      renderGoogleClassroomWidget(
        buildGoogleClassroomWidgetId(instance.id),
        buildGoogleClassroomOptions(
          assignmentDetails,
          instance.student_deeplink
        )
      )
    );
  }, [assignmentDetails, instancesWithAssignments]);

  return (
    <>
      {map(instancesWithAssignments, (instance) => (
        <div key={instance.id}>
          <div className={styles.header}>
            <Typography variant="B-Text-2" color={colors.grey1}>
              Assigned {calendarDate(instance.created_at)} by{' '}
              {instance.created_by.name}
            </Typography>
            <div className={styles.googleClassroomWidget}>
              <div id={buildGoogleClassroomWidgetId(instance.id)}></div>
              <Typography variant="S-TEXT-1" color={colors.blueDark}>
                Share to Google Classroom
              </Typography>
            </div>
            <StudentLinkCopyButton deeplink={instance.student_deeplink} />
          </div>
          {map(instance.assignments, (assignment) => (
            <div key={assignment.id} className={styles.assignment}>
              {getAssignmentName(assignment)}
            </div>
          ))}
        </div>
      ))}
    </>
  );
};
